import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { HookStyle } from '../../../api/client';
import { Btn, Card, Empty, Foot, THead, TRow, TextInput, XButton } from './kit';
import { C } from './tokens';

const COLS = '160px minmax(0,1fr) 26px';

/** Editable Label / Prompt table. Prompts are one-line inputs; the full text
 *  shows in the tooltip. Empty list = the AI picks any natural approach. */
export function HooksTable({ hooks, onChange, promptPlaceholder, emptyText, footExtra, footHint }: {
  hooks: HookStyle[];
  onChange: (next: HookStyle[]) => void;
  promptPlaceholder: string;
  emptyText?: string;
  footExtra?: ReactNode;
  footHint?: ReactNode;
}) {
  const { t } = useTranslation('assistantCompact');
  const edit = (i: number, p: Partial<HookStyle>) => onChange(hooks.map((h, k) => (k === i ? { ...h, ...p } : h)));
  return (
    <Card>
      <THead cols={COLS}><span>{t('hooks.label')}</span><span>{t('hooks.prompt')}</span><span /></THead>
      {hooks.map((h, i) => (
        <TRow key={i} cols={COLS}>
          <TextInput value={h.heading} onChange={(v) => edit(i, { heading: v })} placeholder={t('hooks.label')} />
          <TextInput value={h.prompt} title={h.prompt} onChange={(v) => edit(i, { prompt: v })} placeholder={promptPlaceholder} mono />
          <XButton onClick={() => onChange(hooks.filter((_, k) => k !== i))} title={t('remove')} />
        </TRow>
      ))}
      {!hooks.length && <Empty>{emptyText ?? t('hooks.empty')}</Empty>}
      <Foot>
        <Btn kind="dashed" height={26} onClick={() => onChange([...hooks, { heading: '', prompt: '' }])}>{t('hooks.add')}</Btn>
        {footExtra}
        {footHint && <><span style={{ flex: 1 }} /><span style={{ color: C.muted }}>{footHint}</span></>}
      </Foot>
    </Card>
  );
}
